import {useState, useMemo} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {requestPassword} from '../core/_requests'
import Swal from 'sweetalert2'
import '../AuthPageStyle.scss'

const initialValues = {
  phone: '',
}

export function ForgotPassword() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)

  const forgotPasswordSchema = useMemo(
    () =>
      Yup.object().shape({
        phone: Yup.string()
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.PHONE_REQUIRED'}))
          .test(
            'is-valid-identifier',
            intl.formatMessage({id: 'AUTH.VALIDATION.PHONE_INVALID'}),
            (value) => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              const phoneRegex = /^09\d{9}$/
              return emailRegex.test(value || '') || phoneRegex.test(value || '')
            }
          ),
      }),
    [intl]
  )

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        requestPassword(values.phone)
          .then(({data}) => {
            if (data.status) {
              Swal.fire({
                title: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}),
                text: data.message,
                icon: 'success',
                confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
              })
              setHasErrors(false)
            } else {
              if (data.error == 1) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INCOMPLETE'}),
                  text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INCOMPLETE_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else if (data.error == 2) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INVALID_CONTACT'}),
                  text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INVALID_CONTACT_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else if (data.error == 3) {
                const notFoundMsg = intl.formatMessage(
                  {id: 'AUTH.VALIDATION.NOT_FOUND'},
                  {name: intl.formatMessage({id: 'AUTH.INPUT.PHONE'})}
                )
                Swal.fire({
                  title: notFoundMsg,
                  text: notFoundMsg,
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
                  text: intl.formatMessage({id: 'COMMON.ERROR_GENERIC'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              }
              setHasErrors(true)
            }
            setSubmitting(false)
            setLoading(false)
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus(intl.formatMessage({id: 'AUTH.VALIDATION.INVALID_LOGIN'}))
          })
      }, 1000)
    },
  })

  return (
    <div className='form-wrapper'>
      <form
        className='form form-inner p-5 mt-3'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_password_reset_form'
      >
        <div className='text-center mb-11'>
          <h1 className='fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.FORGOT.TITLE'})}</h1>
        </div>
        {hasErrors == false && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info'>{intl.formatMessage({id: 'AUTH.FORGOT.SUCCESS'})}</div>
          </div>
        )}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.PHONE'})}</label>
          <input
            type='phone'
            placeholder={intl.formatMessage({id: 'AUTH.LOGIN.PHONE_PLACEHOLDER'})}
            autoComplete='off'
            {...formik.getFieldProps('phone')}
            className={clsx(
              'form-control bg-transparent custom-input',
              {'is-invalid': formik.touched.phone && formik.errors.phone},
              {'is-valid': formik.touched.phone && !formik.errors.phone}
            )}
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.phone}</span>
              </div>
            </div>
          )}
        </div>
        <div className='d-grid mb-10 mt-4'>
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='custom-btn btn-custom-font btn-custom-color rounded-pill btn-lg w-auto'
            disabled={loading || formik.isSubmitting}
          >
            {!loading ? (
              <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.FORGOT.TITLE'})}</span>
            ) : (
              <span className='indicator-progress' style={{display: 'block'}}>
                {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        <div className='text-center fs-6'>
          <Link to='/auth/login' className='link custom-primary-color'>
            {intl.formatMessage({id: 'AUTH.GENERAL.BACK_BUTTON'})}
          </Link>
        </div>
      </form>
    </div>
  )
}
